import logging from '../lib/logging';
import fb from '../lib/facebook';
import oneApi from './one-api';
import hofCookie from '../lib/cookie-data';
import storage from '../lib/local-storage';
import {
	GtmEvens,
	GtmEventParameters,
	GtmFallbackVal,
	GtmFunnelStep,
	GtmThirdParty,
	prepareGtmPayload,
} from './gtm';

export default class HofUser {
	static FB_TYPE = 'facebook';
	static ONE_TYPE = 'one';
	static DOUBLE_TYPE = 'double';
	static LOGIN_SRC_COOKIE = 'hof.login_src';
	static LOBBY_INIT_GTM_COOKIE = 'hof.lobby.init.gtm';

	/**
	 * @type {'double'|'facebook'|'one'|undefined}
	 */
	#accountType = undefined;
	/**
	 * @type {string|undefined}
	 */
	#fbSignedRequest = undefined;
	/**
	 * @type {string|undefined}
	 */
	#fbAccessToken = undefined;
	/**
	 * @type {string|undefined}
	 */
	#oneAccessToken = undefined;
	/**
	 * @type {string|undefined}
	 */
	#oneRefreshToken = undefined;
	/**
	 * @type {number|undefined}
	 */
	#oneExpiresIn = undefined;
	/**
	 * @type {string|undefined}
	 */
	#loginSrc = undefined;
	/**
	 * @type {boolean}
	 */
	#isLobbyInitGtmSent = false;
	/**
	 * @type {Map<THofUserProvider, Function>}
	 */
	#logOutMap = new Map();
	/**
	 * @type {Map<THofUserProvider, Function>}
	 */
	#logInMap = new Map();

	constructor() {
		this.#initFlowMap();

		this.getCredentials = this.getCredentials.bind(this);

		this.init = this.init.bind(this);
		this.logout = this.logout.bind(this);
		this.login = this.login.bind(this);
		this.setLobbyInitGtm = this.setLobbyInitGtm.bind(this);
		this.removeProvider = this.removeProvider.bind(this);
	}

	/**
	 * @returns {boolean}
	 */
	get isLobbyInitGtmSent() {
		return this.#isLobbyInitGtmSent;
	}

	get fbAccessToken() {
		return this.#fbAccessToken;
	}

	get oneAccessToken() {
		return this.#oneAccessToken;
	}

	get isFacebookUser() {
		return this.isLoggedIn && this.#accountType === HofUser.FB_TYPE;
	}

	get isOneUser() {
		return this.isLoggedIn && this.#accountType === HofUser.ONE_TYPE;
	}

	get isDoubleUser() {
		return this.isLoggedIn && this.#accountType === HofUser.DOUBLE_TYPE;
	}

	/**
	 * @returns {boolean}
	 */
	get isLoggedIn() {
		return (
			typeof this.#accountType !== 'undefined' &&
			[HofUser.FB_TYPE, HofUser.ONE_TYPE, HofUser.DOUBLE_TYPE].includes(
				this.#accountType
			)
		);
	}

	/**
	 * @returns {boolean}
	 */
	get isLoggedInFromSite() {
		return this.#loginSrc === 'web';
	}

	#initFlowMap() {
		// FB flow

		this.#logOutMap.set(
			HofUser.FB_TYPE,
			async () =>
				new Promise(async (resolve, reject) => {
					try {
						const fbLoginStatus = await fb.getLoginStatus();

						if (fbLoginStatus?.status === 'connected') {
							await fb.logout();
						}

						resolve(true);
					} catch (e) {
						reject(e);
					}
				})
		);

		this.#logInMap.set(
			HofUser.FB_TYPE,
			async (fbOptions) =>
				new Promise(async (resolve, reject) => {
					try {
						const response = await fb.login(fbOptions);

						if (response.status !== 'connected') {
							reject(response);
							return;
						}

						resolve(response);
					} catch (e) {
						reject(e);
					}
				})
		);

		// OneConnect flow
		this.#logOutMap.set(HofUser.ONE_TYPE, () => oneApi.logout());
		this.#logInMap.set(HofUser.ONE_TYPE, () => oneApi.logIn());
	}

	async init() {
		const fbSessionData = await fb.readSessionData();
		const oneConnectSession = await oneApi.readSessionData();

		this.#oneAccessToken = oneConnectSession?.oneAccessToken;
		this.#oneRefreshToken = oneConnectSession?.oneRefreshToken;
		this.#oneExpiresIn = oneConnectSession?.oneExpiresIn;

		this.#fbSignedRequest = fbSessionData?.signedRequest;
		this.#fbAccessToken = fbSessionData?.accessToken;

		if (this.#fbSignedRequest && this.#oneAccessToken) {
			this.#accountType = HofUser.DOUBLE_TYPE;
		} else if (this.#fbSignedRequest) {
			this.#accountType = HofUser.FB_TYPE;
		} else if (this.#oneAccessToken) {
			this.#accountType = HofUser.ONE_TYPE;
		} else {
			this.#accountType = undefined;
		}

		this.#loginSrc = hofCookie.get(HofUser.LOGIN_SRC_COOKIE);

		this.#isLobbyInitGtmSent = Boolean(
			hofCookie.get(HofUser.LOBBY_INIT_GTM_COOKIE)
		);

		logging('HofUser', () => {
			console.info({
				accountType: this.#accountType,
				fbSignedRequest: this.#fbSignedRequest,
				fbAccessToken: this.#fbAccessToken,
				oneAccessToken: this.#oneAccessToken,
				oneRefreshToken: this.#oneRefreshToken,
				oneExpiresIn: this.#oneExpiresIn,
				isLoggedIn: this.isLoggedIn,
				loginSrc: this.#loginSrc,
				isLobbyInitGtmSent: this.#isLobbyInitGtmSent,
			});
		});
	}

	#setLoginSource() {
		hofCookie.set(HofUser.LOGIN_SRC_COOKIE, 'web');
	}

	async setLobbyInitGtm() {
		hofCookie.set(HofUser.LOBBY_INIT_GTM_COOKIE, 1);

		await this.init();
	}

	#clearLoginSource() {
		hofCookie.remove(HofUser.LOBBY_INIT_GTM_COOKIE);
		hofCookie.remove(HofUser.LOGIN_SRC_COOKIE);
	}

	/**
	 *
	 * @returns {Array<THofUserFbCredentials | THofUserOneCredentials>}
	 */
	getCredentials() {
		const oneCreds = () => ({
			type: HofUser.ONE_TYPE,
			value: this.#oneAccessToken,
			refresh_token: this.#oneRefreshToken,
			expires_in: this.#oneExpiresIn,
		});

		const fbCreds = () => ({
			type: HofUser.FB_TYPE,
			value: this.#fbSignedRequest,
			value_at: this.#fbAccessToken,
		});

		if (this.isDoubleUser) {
			return [oneCreds(), fbCreds()];
		}

		if (this.isFacebookUser) {
			return [fbCreds()];
		}

		if (this.isOneUser) {
			return [oneCreds()];
		}

		return [];
	}

	/**
	 * @param {THofUserProvider|undefined} provider
	 * @param {boolean} isLogoutFromSite
	 * @returns {Promise<boolean>}
	 */
	async logout(provider = undefined, isLogoutFromSite = false) {
		return new Promise(async (resolve, reject) => {
			if (!this.isLoggedIn) {
				reject('user is not logged in');
				return;
			}

			logging(
				'logout',
				() => {
					console.log('provider', provider);
				},
				'info'
			);

			if (typeof provider !== 'undefined') {
				if (!this.#logOutMap.has(provider)) {
					reject(`Invalid provider: ${provider}`);
					return;
				}

				try {
					const fn = this.#logOutMap.get(provider);
					await fn.apply(this);
				} catch (e) {
					reject(e);
					return;
				}
			} else {
				const fbLogout = this.#logOutMap.get(HofUser.FB_TYPE);
				const oneLogout = this.#logOutMap.get(HofUser.ONE_TYPE);

				if (this.isDoubleUser) {
					await fbLogout.apply(this);
					await oneLogout.apply(this);
				} else if (this.isFacebookUser) {
					await fbLogout.apply(this);
				} else if (this.isOneUser) {
					await oneLogout.apply(this);
				}
			}

			if (isLogoutFromSite) {
				this.#clearLoginSource();
			}

			await this.init();

			resolve(true);
		});
	}

	/**
	 * @param {THofUserProvider} provider
	 * @param {facebook.LoginOptions|undefined} fbOptions
	 * @param {boolean|undefined} isLoginFromSite
	 * @returns {Promise<facebook.StatusResponse|OneConnectAuthDataSession>}
	 */
	async login(provider, fbOptions = {}, isLoginFromSite = false) {
		return new Promise(async (resolve, reject) => {
			if (!this.#logInMap.has(provider)) {
				reject(`Invalid provider: ${provider}`);
				return;
			}

			const fn = this.#logInMap.get(provider);

			try {
				const result = await fn(fbOptions);

				if (isLoginFromSite) {
					this.#setLoginSource();

					if (provider === HofUser.FB_TYPE) {
						storage.set(
							GtmEvens.FUNNEL_FINISH,
							prepareGtmPayload({
								[GtmEventParameters.PROCESS_NAME]: 'register',
								[GtmEventParameters.CLICK_TEXT]: 'log-in',
								[GtmEventParameters.IS_SIGN_UP]: 0,
								[GtmEventParameters.THIRD_PARTY_NAME]: GtmThirdParty.FB,
								[GtmEventParameters.THIRD_PARTY_SIGN_UP]: GtmFallbackVal.EMPTY,
								[GtmEventParameters.THIRD_PARTY_LOGIN]: GtmFallbackVal.EMPTY,
								[GtmEventParameters.PRE_REG_USER_ID]: GtmFallbackVal.EMPTY,

								...GtmFunnelStep.get(GtmFunnelStep.REGISTER_SUCCESS),
							})
						);
					}
				}

				await this.init();

				resolve(result);
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * @deprecated
	 * @param {THofUserProvider} provider
	 * @returns {Promise<boolean>}
	 */
	async removeProvider(provider) {
		return new Promise(async (resolve, reject) => {
			if (!this.#logOutMap.has(provider)) {
				reject(`Invalid provider: ${provider}`);
				return;
			}

			try {
				const fn = this.#logOutMap.get(provider);
				fn.apply(null);

				await this.init();

				resolve(true);
			} catch (e) {
				reject(e);
			}
		});
	}
}
